/**
 * Single implementation of "apply a translation/rotation/scale to an ODA entity".
 *
 * Before this module the same matrix-building block existed in five places —
 * BaseHandle.applyAccumulatedTransformToEntity(), BaseHandle.setGhostTransforms(),
 * BaseHandle.cancel()'s non-ghost fallback, the (dead)
 * BaseHandle.applyAccumulatedTransformsToOriginals(), and
 * TransformEntityCommand.applyTransforms() — which had already drifted apart
 * (only some of them counter-rotated photo marker text). Everything that builds
 * an OdGe* matrix and calls transformBy() now goes through here.
 *
 * Scope: this module is *pure* — it opens/transforms/frees entities and nothing
 * else. It deliberately knows nothing about ghosts, views, the render pipeline
 * or command history; that lifecycle logic stays with its callers.
 */

import type { DrawingModuleType } from "../DrawingSingleton";
import type { DrawingModule } from "../types/DrawingWeb";
import type { Point3D } from "./types";
import { deleteObj } from "../core/delete";
import { openAs } from "../core/oda";
import { counterRotatePhotoMarkerText } from "../photomarker";

/** OdDb::kForWrite — matches the literal BaseHandle passes to openAs(). */
const OPEN_FOR_WRITE = 1 as DrawingModule.OpenMode;

/**
 * A translation + rotation + scale, each around its own center.
 *
 * All three channels are always present; unused ones hold their neutral value
 * (zero vector / zero angle / unit scale) so callers never deal with partial
 * objects. A null center disables its channel even if the value is non-neutral,
 * matching the original `rotation !== 0 && rotationCenter` guards.
 */
export interface EntityTransform {
  translation: Point3D;
  rotation: number;
  rotationCenter: Point3D | null;
  scale: Point3D;
  scaleCenter: Point3D | null;
}

export const IDENTITY_TRANSFORM: Readonly<EntityTransform> = Object.freeze({
  translation: { x: 0, y: 0, z: 0 },
  rotation: 0,
  rotationCenter: null,
  scale: { x: 1, y: 1, z: 1 },
  scaleCenter: null,
});

/**
 * One entity and the transform applied to it — the params
 * TransformEntityCommand keeps for undo/redo.
 *
 * The transform is stored as-is rather than flattened into scalars, so there is
 * no encoding to round-trip through and no way for the stored form to drift
 * from the one that was applied.
 */
export interface EntityTransformParams {
  handle: string;
  transform: EntityTransform;
}

/**
 * Order in which the three channels are applied.
 *
 * `forward` is translation → rotation → scale (what every forward call site
 * used). `reverse` is scale → rotation → translation, which is the correct way
 * to undo a `forward` composition and is what BaseHandle.cancel() has always
 * done.
 *
 * NOTE: TransformEntityCommand.undo() inverts values but keeps `forward` order,
 * which is only mathematically sound when a single channel is non-neutral.
 * That is preserved here rather than silently corrected — changing it would be
 * a behaviour change to undo/redo and belongs in its own commit.
 */
export type TransformOrder = "forward" | "reverse";

export interface ApplyTransformOptions {
  /** Apply the inverse: negate translation/rotation, reciprocate scale. */
  invert?: boolean;
  /** Channel order. Defaults to "forward". */
  order?: TransformOrder;
}

// ============== Construction helpers ==============

export function createTransform(
  partial: Partial<EntityTransform> = {},
): EntityTransform {
  return {
    translation: partial.translation ?? { x: 0, y: 0, z: 0 },
    rotation: partial.rotation ?? 0,
    rotationCenter: partial.rotationCenter ?? null,
    scale: partial.scale ?? { x: 1, y: 1, z: 1 },
    scaleCenter: partial.scaleCenter ?? null,
  };
}

/**
 * Deep copy.
 *
 * Params handed to a command outlive the handle's own state, which start() and
 * cancel() reset, so they must not alias its points. Flattening into number[]
 * used to do this implicitly; storing the transform itself makes it explicit.
 */
export function snapshotTransform(t: EntityTransform): EntityTransform {
  return {
    translation: { ...t.translation },
    rotation: t.rotation,
    rotationCenter: t.rotationCenter ? { ...t.rotationCenter } : null,
    scale: { ...t.scale },
    scaleCenter: t.scaleCenter ? { ...t.scaleCenter } : null,
  };
}

export function translationTransform(
  dx: number,
  dy: number,
  dz = 0,
): EntityTransform {
  return createTransform({ translation: { x: dx, y: dy, z: dz } });
}

export function rotationTransform(
  angleRadians: number,
  center: Point3D,
): EntityTransform {
  return createTransform({ rotation: angleRadians, rotationCenter: center });
}

export function uniformScaleTransform(
  factor: number,
  center: Point3D,
): EntityTransform {
  return createTransform({
    // z is scaled too, not left at 1: OdDbPolyline.transformBy() rejects a
    // non-uniform 3D scaling matrix (eCannotScaleNonUniformly) and silently
    // no-ops, so a rectangle would not scale at all. A z-scale about a z=0
    // centre is visually inert on these planar drawings.
    scale: { x: factor, y: factor, z: factor },
    scaleCenter: center,
  });
}

// ============== Channel predicates ==============

function hasTranslation(t: EntityTransform): boolean {
  return (
    t.translation.x !== 0 || t.translation.y !== 0 || t.translation.z !== 0
  );
}

function hasRotation(t: EntityTransform): boolean {
  return t.rotation !== 0 && t.rotationCenter !== null;
}

function hasScale(t: EntityTransform): boolean {
  return (
    (t.scale.x !== 1 || t.scale.y !== 1 || t.scale.z !== 1) &&
    t.scaleCenter !== null
  );
}

/** Whether this transform would change anything at all. */
export function isIdentityTransform(t: EntityTransform): boolean {
  return !hasTranslation(t) && !hasRotation(t) && !hasScale(t);
}

// ============== Primitive appliers ==============

function applyTranslationChannel(
  module: DrawingModuleType,
  entity: DrawingModule.OdDbEntity,
  translation: Point3D,
  invert: boolean,
): void {
  const sign = invert ? -1 : 1;
  let vec: DrawingModule.OdGeVector3d | null = null;
  let matrix: DrawingModule.OdGeMatrix3d | null = null;
  try {
    vec = new module.OdGeVector3d(
      sign * translation.x,
      sign * translation.y,
      sign * translation.z,
    );
    matrix = module.OdGeMatrix3d.translation(vec);
    entity.transformBy(matrix);
  } finally {
    if (matrix) deleteObj(matrix);
    if (vec) deleteObj(vec);
  }
}

/** @returns the signed angle actually applied (for photo marker counter-rotation). */
function applyRotationChannel(
  module: DrawingModuleType,
  entity: DrawingModule.OdDbEntity,
  rotation: number,
  rotationCenter: Point3D,
  invert: boolean,
): number {
  const angle = invert ? -rotation : rotation;
  let center: DrawingModule.OdGePoint3d | null = null;
  let axis: DrawingModule.OdGeVector3d | null = null;
  let matrix: DrawingModule.OdGeMatrix3d | null = null;
  try {
    center = new module.OdGePoint3d(
      rotationCenter.x,
      rotationCenter.y,
      rotationCenter.z,
    );
    axis = new module.OdGeVector3d(0, 0, 1);
    matrix = module.OdGeMatrix3d.rotation(angle, axis, center);
    entity.transformBy(matrix);
  } finally {
    if (matrix) deleteObj(matrix);
    if (center) deleteObj(center);
    if (axis) deleteObj(axis);
  }
  return angle;
}

function applyScaleChannel(
  module: DrawingModuleType,
  entity: DrawingModule.OdDbEntity,
  scale: Point3D,
  scaleCenter: Point3D,
  invert: boolean,
): void {
  let center: DrawingModule.OdGePoint3d | null = null;
  let scaleObj: DrawingModule.OdGeScale3d | null = null;
  let matrix: DrawingModule.OdGeMatrix3d | null = null;
  try {
    center = new module.OdGePoint3d(
      scaleCenter.x,
      scaleCenter.y,
      scaleCenter.z,
    );
    scaleObj = new module.OdGeScale3d(
      invert ? 1 / scale.x : scale.x,
      invert ? 1 / scale.y : scale.y,
      invert ? 1 / scale.z : scale.z,
    );
    matrix = new module.OdGeMatrix3d();
    matrix.setToScaling1(scaleObj, center);
    entity.transformBy(matrix);
  } finally {
    if (matrix) deleteObj(matrix);
    if (scaleObj) deleteObj(scaleObj);
    if (center) deleteObj(center);
  }
}

// ============== Public appliers ==============

/**
 * Apply a transform to an already-open entity — typically a view ghost, which
 * has no OdDbObjectId and therefore needs neither recordGraphicsModified() nor
 * photo marker counter-rotation.
 *
 * @returns the signed rotation angle applied, or 0 if the rotation channel was
 * inactive — callers holding the entity's id use it to counter-rotate text.
 */
export function applyTransformToEntity(
  module: DrawingModuleType,
  entity: DrawingModule.OdDbEntity,
  t: EntityTransform,
  options: ApplyTransformOptions = {},
): number {
  const invert = options.invert ?? false;
  const order = options.order ?? "forward";

  let appliedAngle = 0;

  const translation = () => {
    if (hasTranslation(t)) {
      applyTranslationChannel(module, entity, t.translation, invert);
    }
  };
  const rotation = () => {
    if (hasRotation(t) && t.rotationCenter) {
      appliedAngle = applyRotationChannel(
        module,
        entity,
        t.rotation,
        t.rotationCenter,
        invert,
      );
    }
  };
  const scale = () => {
    if (hasScale(t) && t.scaleCenter) {
      applyScaleChannel(module, entity, t.scale, t.scaleCenter, invert);
    }
  };

  if (order === "forward") {
    translation();
    rotation();
    scale();
  } else {
    scale();
    rotation();
    translation();
  }

  return appliedAngle;
}

export interface ApplyToIdsOptions extends ApplyTransformOptions {
  /**
   * Call recordGraphicsModified(true) after transforming. Defaults to true.
   * Set false where the caller marks the entity itself afterwards (BaseHandle
   * .end() does this in its own loop).
   */
  recordGraphics?: boolean;
}

/**
 * Apply a transform to database entities: open for write, transform,
 * counter-rotate any photo marker text so it stays horizontal, and mark the
 * graphics modified.
 */
export function applyTransformToEntityIds(
  module: DrawingModuleType,
  entityIds: DrawingModule.OdDbObjectId[],
  t: EntityTransform,
  options: ApplyToIdsOptions = {},
): void {
  const recordGraphics = options.recordGraphics ?? true;

  for (const entityId of entityIds) {
    const entity = openAs(entityId, module.OdDbEntity, OPEN_FOR_WRITE);
    try {
      const appliedAngle = applyTransformToEntity(module, entity, t, options);

      if (appliedAngle !== 0) {
        counterRotatePhotoMarkerText(module, entityId, appliedAngle);
      }
      if (recordGraphics) {
        entity.recordGraphicsModified(true);
      }
    } finally {
      deleteObj(entity);
    }
  }
}

/**
 * Difference between two absolute transforms, as a transform that takes an
 * entity already showing `from` to showing `to`.
 *
 * Translation and rotation subtract; scale divides. Centers come from `to`
 * (they are fixed for the whole of any one interaction). Applying frame-to-frame
 * deltas rather than rebuilding the entity from scratch is what keeps ghost
 * previews cheap and drift-free.
 */
export function transformDelta(
  from: EntityTransform,
  to: EntityTransform,
): EntityTransform {
  return {
    translation: {
      x: to.translation.x - from.translation.x,
      y: to.translation.y - from.translation.y,
      z: to.translation.z - from.translation.z,
    },
    rotation: to.rotation - from.rotation,
    rotationCenter: to.rotationCenter,
    scale: {
      x: from.scale.x !== 0 ? to.scale.x / from.scale.x : to.scale.x,
      y: from.scale.y !== 0 ? to.scale.y / from.scale.y : to.scale.y,
      z: from.scale.z !== 0 ? to.scale.z / from.scale.z : to.scale.z,
    },
    scaleCenter: to.scaleCenter,
  };
}

/**
 * Move a set of live entities (view ghosts) from showing `from` to showing `to`.
 */
export function applyTransformDelta(
  module: DrawingModuleType,
  entities: DrawingModule.OdDbEntity[],
  from: EntityTransform,
  to: EntityTransform,
): void {
  if (entities.length === 0) return;
  const delta = transformDelta(from, to);
  if (isIdentityTransform(delta)) return;

  for (const entity of entities) {
    applyTransformToEntity(module, entity, delta);
  }
}
